import {Combobox} from '@headlessui/react';
import React, {useEffect, useRef, useState} from 'react';
import IconName from "types/IconName";
import {CheckIcon, SelectorIcon} from "@heroicons/react/solid";
import {concatenate} from "services/string";
import Icon from "components/Icon";
import Spinner from "components/Spinner";
import IconBadge from "components/IconBadge";
import {useQuery} from "react-query";

// Helpers ----

function compareOptions(a: Option, b: Option) {
    if (!a || !b) return false;

    if (a?.type && b?.type) {
        return a.id === b.id && a.type === b.type;
    }

    return a.id === b.id;
}

// Types ----

export type Option = {
    type?: string
    id: number | string
    name: string
    description?: string | null
    icon?: IconName | null
    indent?: boolean
}

export type OptionSource = (query: string, selectedOptions: Array<Option>) => Array<Option> | Promise<Array<Option>>

type BaseProps = {
    label?: string | null
    hint?: string | null
    source: OptionSource
    queryWait?: number
    multiple?: boolean
    max?: number
    required?: boolean
    disabled?: boolean
    onNew?: (value: string) => Promise<Option | null>
    exactMatch?: boolean
}

type SingleProps = BaseProps & {
    multiple?: false | undefined
    selected?: Option | null
    onChange?: (selected: Option) => any
}

type MultipleProps = BaseProps & {
    multiple: true
    selected?: Array<Option> | null
    onChange?: (selected: Array<Option>) => any
}

type Props = SingleProps | MultipleProps

// Component ----

function Select({selected, source, label, hint, multiple, max, queryWait, onChange, onNew, exactMatch, required, disabled}: Props) {
    const [query, setQuery] = useState<string>('');
    const [requestedQuery, setRequestedQuery] = useState<string>('');
    const queryTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

    const {data: options, isLoading, isFetching, refetch} = useQuery(
        ['search', label, requestedQuery],
        async () => source(requestedQuery, selectedOptions),
        {keepPreviousData: true}
    )

    const [selectedOptions, setSelectedOptions] = useState<Array<Option>>(selected? [selected].flat() : []);

    const emptyOption = {id: 'none', name: 'None', icon: IconName.X}
    const newOption = {id: 'add-option', name: "Add Option", icon: IconName.PLUS}

    // Handle querying the data source
    useEffect(() => {
        if(!isLoading) {
            clearTimeout(queryTimeout.current)

            if(query){
                queryTimeout.current = setTimeout(() => setRequestedQuery(query), queryWait ?? 300)
            } else {
                setRequestedQuery('')
            }
        }
        // eslint-disable-next-line
    }, [query]);

    // Handle the selected options being changed from the external component
    useEffect(() => {
        setSelectedOptions(selected? [selected].flat() : [])
    }, [selected]);

    const deselectOption = (option: Option) => {
        const selected = selectedOptions.filter(selectedOption => selectedOption.id !== option.id)

        setSelectedOptions(selected)

        if(onChange){
            if(multiple) {
                onChange(selected)
            } else {
                onChange(selected[0])
            }
        }
    }

    const handleSingleChange = async (selected: Option) => {
        let chosenOption = selected

        // Add the new option and refetch the list
        if(!!onNew && !!query && selected.id === 'add-option'){
            const newOption = await onNew(query)

            if(newOption){
                chosenOption = newOption
                await refetch()
            }
        }

        // Select the option and clear the current query
        setSelectedOptions([chosenOption])
        setQuery('')

        // Fire onChange handler, if specified
        if(!multiple && onChange) {
            onChange(chosenOption)
        }
    }

    const handleMultipleChange = async (selected: Array<Option>) => {
        let chosenOptions = selected

        // Check for "add-option" in the list of selected options
        const newOptionIndex = selected.findIndex(option => option.id === 'add-option')

        // Add the new option, replace the "add-option" option with it, and refetch the list
        if(!!onNew && !!query && newOptionIndex > -1){
            const newOption = await onNew(query)

            if(newOption){
                chosenOptions[newOptionIndex] = newOption
                await refetch()
            }
        }

        if(!max || max >= chosenOptions.length){
            // Select the option and clear the current query
            setSelectedOptions(chosenOptions)
            setQuery('')

            // Fire onChange handler, if specified
            if(multiple && onChange) {
                onChange(chosenOptions)
            }
        }
    }

    const generateDisplayValue = (selected: Option | Array<Option>) => {
        return Array.isArray(selected)? `${selected.length} selected` : `${selected.name}${selected.description ? ` - ${selected.description}` : ''}`
    }

    const inputKey = (isOpen: boolean) => selectedOptions.map(option => option.id).join('') + isOpen.toString()

    const contents = ({open}: {open: boolean}) => (<>
        {/* Label */}
        <div className="flex justify-between">
            {!!label && <Combobox.Label className="block text-sm font-medium text-gray-700">{label}</Combobox.Label>}
            {!!hint && <span className="text-sm text-gray-500">{hint}</span>}
        </div>

        {/* Body */}
        <div className="relative mt-1">

            {/* Query Input */}
            <Combobox.Input
                key={inputKey(open)}
                className={concatenate(disabled && 'text-gray-300', 'w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-brand focus:outline-none focus-visible:ring-1 focus-visible:ring-brand sm:text-sm')}
                onChange={event => setQuery(event.target.value)}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {setQuery(""); event.target.select()}}
                displayValue={generateDisplayValue}
                autoComplete="off"
                required={required}
            />

            {/* List Button */}
            <Combobox.Button
                className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                {!disabled && isFetching ?
                    <Spinner color={Spinner.colors.BRAND} size={Spinner.sizes.X_SMALL}/> :
                    <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                }
            </Combobox.Button>

            {/* Options List */}
            {options && (
                <Combobox.Options
                    className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">

                    {/* None option */}
                    {!required &&
                        <Combobox.Option
                            key={emptyOption.id}
                            value={emptyOption}
                            className={({active}) => concatenate(
                                'relative cursor-default select-none py-2 pl-3 pr-9',
                                active ? 'bg-brand text-white' : 'text-gray-900'
                            )}
                        >
                            {({active, selected}) => (
                                <div className="flex items-center">
                                    <Icon
                                        name={emptyOption.icon}
                                        className={concatenate(active ? 'text-gray-100' : 'text-gray-500', selected && 'font-semibold', 'h-4 w-4 flex-shrink-0')}
                                    />

                                    <span className={concatenate('ml-3 truncate flex-shrink-0', selected && 'font-semibold')}>
                                        None
                                    </span>
                                </div>
                            )}
                        </Combobox.Option>
                    }

                    {/* Add option */}
                    {!!onNew && !!query && (exactMatch? !options.find(option => option.name === query) : true) &&
                        <Combobox.Option
                            key="add-option"
                            value={newOption}
                            className={({active}) => concatenate(
                                'relative cursor-default select-none py-2 pl-3 pr-9',
                                active ? 'bg-brand text-white' : 'text-gray-900'
                            )}
                        >
                            {({active, selected}) => (
                                <div className="flex items-center">
                                    <Icon
                                        name={newOption.icon}
                                        className={concatenate(active ? 'text-gray-100' : 'text-gray-500', selected && 'font-semibold', 'h-4 w-4 flex-shrink-0')}
                                    />

                                    <span className={concatenate('ml-3 truncate flex-shrink-0', selected && 'font-semibold')}>
                                        Add "{query}"
                                    </span>
                                </div>
                            )}
                        </Combobox.Option>
                    }

                    {/* Single Option */}
                    {Array.from(options).map((option) => (
                        <Combobox.Option
                            key={(option.type || '') + option.id}
                            value={option}
                            title={`${option.name}${option.description ? ` - ${option.description}` : ''}`}
                            className={({active}) => concatenate(
                                'relative cursor-default select-none py-2 pl-3 pr-9',
                                active ? 'bg-brand text-white' : 'text-gray-900'
                            )}
                        >
                            {({active, selected}) => (
                                <>
                                    <div className="flex items-center">

                                        {/* Indent arrow */}
                                        {!!option.indent &&
                                            <span className="ml-2 mr-2">↳</span>
                                        }

                                        {/* Option Icon */}
                                        {!!option.icon &&
                                            <Icon
                                                name={option.icon}
                                                className={concatenate(active ? 'text-gray-100' : 'text-gray-500', selected && 'font-semibold', 'h-4 w-4 flex-shrink-0')}
                                            />
                                        }

                                        {/* Option Name */}
                                        <span className={concatenate('ml-3 truncate flex-shrink-0', selected && 'font-semibold')}>
                                            {option.name}
                                        </span>

                                        {/* Option description */}
                                        {option.description &&
                                            <span
                                                className={concatenate(
                                                    'ml-4 sm:ml-2 truncate text-gray-500 hidden md:inline',
                                                    active ? 'text-gray-200' : 'text-gray-500'
                                                )}
                                            >
                                                {option.description}
                                            </span>
                                        }
                                    </div>

                                    {selected && (
                                        <span
                                            className={concatenate(
                                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                                active ? 'text-white' : 'text-brand'
                                            )}
                                        >
                                                <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                        </span>
                                    )}
                                </>
                            )}
                        </Combobox.Option>
                    ))}
                </Combobox.Options>
            )}
        </div>

        {/* Tokens */}
        {multiple &&
            <div className="-mt-2 pt-4 pb-1 px-2 bg-gray-50 rounded-b-lg shadow-sm">
                {selectedOptions.map(option => (
                    <IconBadge
                        key={option.id}
                        color={IconBadge.colors.BRAND}
                        size={IconBadge.sizes.LARGE}
                        icon={option.icon ?? undefined}
                        text={option.name}
                        onDismiss={() => deselectOption(option)}
                    />
                ))}
            </div>
        }
    </>)

    return multiple? (
        <Combobox as="div" by={compareOptions} value={selectedOptions} onChange={handleMultipleChange} multiple disabled={disabled}>
            {contents}
        </Combobox>
    ) : (
        <Combobox as="div" by={compareOptions} value={selectedOptions[0]} onChange={handleSingleChange} disabled={disabled}>
            {contents}
        </Combobox>
    );
}

export default Select;